import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import useResponsive from "../../Hooks/useResponsive";

// Removed axios import to use native fetch
import apiConfig from "../../config";

const ContactQuoteSection = () => {
  const { isExtraLarge } = useResponsive();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    serviceInterest: "Interior Design Consultation",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Basic validation
    if (!formData.fullName || !formData.email || !formData.message) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields.",
        severity: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${apiConfig.apiUrl}/forms/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.status) {
        setSnackbar({
          open: true,
          message:
            "Thanks for contacting us! Our team will get back to you shortly.",
          severity: "success",
        });

        setFormData({
          fullName: "",
          email: "",
          phoneNumber: "",
          serviceInterest: "Interior Design Consultation",
          message: "",
        });
      } else {
        throw new Error(data.message || "Submission failed");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to submit enquiry. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#FCF9F8",
        pt: { xs: "64px", md: "80px", lg: "128px" },
        pb: { xs: "64px", md: "80px", lg: "128px" },
        mx: "auto",
      }}
    >
      {/* Inner Box */}
      <Box
        sx={{
          width: { xs: "100%", md: "90%", lg: "1216px", xl: "1400px" },
          borderRadius: { xs: "0px", md: "16px", lg: "32px" },
          boxShadow: "0px 25px 50px -12px rgba(28, 25, 23, 0.3)",
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          overflow: "hidden",
          mx: "auto",
        }}
      >
        {/* Left Box - Contact Info */}
        <Box
          sx={{
            width: { xs: "100%", lg: "50%", xl: "50%" },
            backgroundColor: "#1C1917",
            p: { xs: "40px 24px", sm: "48px 32px", lg: "80px" },
            display: "flex",
            flexDirection: "column",
            gap: { xs: "24px", lg: "32px" },
          }}
        >
          {/* Header */}
          <Box>
            <Typography
              sx={{
                fontFamily: "Manrope",
                fontWeight: 800,
                fontSize: { xs: "32px", sm: "36px", lg: "44px", xl: "48px" },
                lineHeight: { xs: "40px", sm: "44px", lg: "54px", xl: "58px" },
                color: "#FFFFFF",
                letterSpacing: "-1.2px",
                textAlign: "left",
              }}
            >
              Ready to build your future?
            </Typography>
          </Box>

          {/* Paragraph */}
          <Box>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: { xs: "15px", sm: "16px", lg: "18px" },
                lineHeight: "1.7",
                color: "#D6D3D1",
                textAlign: "left",
              }}
            >
              Whether it's a dream home interior or a large-scale commercial
              project, we're here to consult, design, and deliver.
            </Typography>
          </Box>

          {/* Contact Info with Icons */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              pt: "16px",
            }}
          >
            {/* Email */}
            <Stack direction="row" spacing="16px" alignItems="center">
              <EmailIcon sx={{ fontSize: "22px", color: "#FF5722" }} />
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontSize: { xs: "15px", lg: "18px" },
                  fontWeight: 500,
                  color: "#FFFFFF",
                  "&:hover": { color: "#FF5722" },
                  cursor: "pointer",
                  transition: "color 0.2s ease",
                }}
              >
                abiliorealty@gmail.com
              </Typography>
            </Stack>

            {/* Phone */}
            <Stack direction="row" spacing="16px" alignItems="center">
              <PhoneIcon sx={{ fontSize: "22px", color: "#FF5722" }} />
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontSize: { xs: "15px", lg: "18px" },
                  fontWeight: 500,
                  color: "#FFFFFF",
                  "&:hover": { color: "#FF5722" },
                  cursor: "pointer",
                  transition: "color 0.2s ease",
                }}
              >
                +91 9177640666
              </Typography>
            </Stack>

            {/* Address */}
            <Stack direction="row" spacing="16px" alignItems="flex-start">
              <LocationOnIcon
                sx={{ fontSize: "22px", color: "#FF5722", mt: "2px" }}
              />
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontSize: { xs: "14px", lg: "16px" },
                  fontWeight: 400,
                  lineHeight: "1.6",
                  color: "#D6D3D1",
                  textAlign: "left",
                }}
              >
                101, 1st Floor, Jain Sadguru Images Capital Park, 102A, Capital
                Pk Rd, VIP Hills, Silicon Valley, Madhapur, Hyderabad, Telangana
                500081
              </Typography>
            </Stack>
          </Box>
        </Box>

        {/* Right Box - Form */}
        <Box
          sx={{
            width: { xs: "100%", lg: "50%", xl: "50%" },
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            p: { xs: "40px 20px", sm: "48px 32px", lg: "80px" },
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {/* Form */}
          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: "32px", lg: "24px" },
              width: "100%",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            {/* Name and Email Row - Stack on mobile, Grid on desktop */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: { xs: "32px", lg: "24px" },
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  boxSizing: "border-box",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "#1C1917",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    textAlign: "left",
                  }}
                >
                  Full Name <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="John Doe"
                  variant="outlined"
                  value={formData.fullName}
                  onChange={handleChange("fullName")}
                  required
                  sx={{
                    backgroundColor: "#FFFFFF",
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-root": {
                      height: "50px",
                      borderRadius: "8px",
                      fontFamily: "Inter",
                      fontSize: "14px",
                      color: "#1C1917",
                      "& fieldset": {
                        borderColor: "#D6D3D1",
                      },
                      "&:hover fieldset": {
                        borderColor: "#B02F00",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#B02F00",
                        borderWidth: "2px",
                      },
                    },
                  }}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  boxSizing: "border-box",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "#1C1917",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    textAlign: "left",
                  }}
                >
                  Email Address <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="john@example.com"
                  variant="outlined"
                  type="email"
                  value={formData.email}
                  onChange={handleChange("email")}
                  required
                  sx={{
                    backgroundColor: "#FFFFFF",
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-root": {
                      height: "50px",
                      borderRadius: "8px",
                      fontFamily: "Inter",
                      fontSize: "14px",
                      color: "#1C1917",
                      "& fieldset": {
                        borderColor: "#D6D3D1",
                      },
                      "&:hover fieldset": {
                        borderColor: "#B02F00",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#B02F00",
                        borderWidth: "2px",
                      },
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Service Interest Dropdown */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                boxSizing: "border-box",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: "12px",
                  color: "#1C1917",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  textAlign: "left",
                }}
              >
                Service Interest <span style={{ color: "red" }}>*</span>
              </Typography>
              <FormControl fullWidth sx={{ textAlign: "left" }}>
                <Select
                  value={formData.serviceInterest}
                  onChange={handleChange("serviceInterest")}
                  sx={{
                    height: "50px",
                    backgroundColor: "#FFFFFF",
                    borderRadius: "8px",
                    fontFamily: "Inter",
                    fontSize: "14px",
                    color: "#1C1917",
                    "& .MuiSelect-select": {
                      textAlign: "left",
                      paddingLeft: "14px",
                    },
                    "& .MuiSelect-icon": {
                      color: "#78716C",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D6D3D1",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#B02F00",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#B02F00",
                      borderWidth: "2px",
                    },
                  }}
                  MenuProps={{ disableScrollLock: true }}
                >
                  <MenuItem value="Interior Design Consultation" sx={{ textAlign: "left" }}>
                    Interior Design Consultation
                  </MenuItem>
                  <MenuItem value="Construction Project" sx={{ textAlign: "left" }}>
                    Construction Project
                  </MenuItem>
                  <MenuItem value="Real Estate Inquiry" sx={{ textAlign: "left" }}>
                    Real Estate Inquiry
                  </MenuItem>
                  <MenuItem value="Other Services" sx={{ textAlign: "left" }}>
                    Other Services
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Phone Field */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                boxSizing: "border-box",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: "12px",
                  color: "#1C1917",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  textAlign: "left",
                }}
              >
                Phone Number <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="+91 1234567890"
                variant="outlined"
                value={formData.phoneNumber}
                onChange={handleChange("phoneNumber")}
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    height: "50px",
                    borderRadius: "8px",
                    fontFamily: "Inter",
                    fontSize: "14px",
                    color: "#1C1917",
                    "& fieldset": {
                      borderColor: "#D6D3D1",
                    },
                    "&:hover fieldset": {
                      borderColor: "#B02F00",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#B02F00",
                      borderWidth: "2px",
                    },
                  },
                }}
              />
            </Box>

            {/* Message Box */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                boxSizing: "border-box",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: "12px",
                  color: "#1C1917",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  textAlign: "left",
                }}
              >
                Your Message
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Tell us about your project..."
                variant="outlined"
                value={formData.message}
                onChange={handleChange("message")}
                required
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontFamily: "Inter",
                    fontSize: "14px",
                    color: "#1C1917",
                    padding: "12px",
                    "& fieldset": {
                      borderColor: "#D6D3D1",
                    },
                    "&:hover fieldset": {
                      borderColor: "#B02F00",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#B02F00",
                      borderWidth: "2px",
                    },
                  },
                }}
              />
            </Box>

            {/* Submit Button */}
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading}
              onClick={handleSubmit}
              sx={{
                width: "100%",
                height: "56px",
                borderRadius: "8px",
                background: "linear-gradient(135deg, #B02F00 0%, #FF5722 100%)",
                fontFamily: "Inter",
                fontWeight: 600,
                fontSize: "16px",
                color: "#FFFFFF",
                textTransform: "none",
                mt: { xs: "8px", lg: "8px" },
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #9A2800 0%, #E64A19 100%)",
                },
                "&:disabled": {
                  background: "#ccc",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Send Inquiry"
              )}
            </Button>
          </Box>
        </Box>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactQuoteSection;
